<template>
  <div class="warning-card">
    <!-- 左侧信息区 -->
    <div class="card-left">
      <!-- 标签行 -->
      <div class="tag-row">
        <span class="tag tag-province">{{ item.provinceName }}</span>
        <span class="tag tag-supplier">{{ supplierName }}</span>
      </div>

      <!-- 标题 -->
      <h3 class="card-title">{{ title }}</h3>
      <p class="card-subtitle">{{ subTitle }}</p>

      <!-- 数值区 -->
      <div class="value-row">
        <div class="value-item">
          <p class="value-label">当前{{ isFundMode ? '余额' : '库存' }}</p>
          <p class="value-num">{{ formattedValue }}</p>
        </div>
        <div class="value-item">
          <p class="value-label">日均消耗</p>
          <p class="value-num secondary">{{ formattedDaily }}</p>
        </div>
      </div>
    </div>

    <!-- 右侧天数区 -->
    <div :class="['card-right', daysColorClass]">
      <span class="days-num">{{ item.availableDays }}</span>
      <span class="days-label">可用天数</span>
    </div>
  </div>
</template>

<script>
/**
 * 预警卡片组件
 *
 * 设计模式说明：
 * - 策略模式：通过 isFundMode 属性切换库存/资金两种展示策略
 * - 使用计算属性实现数据格式化，保持模板简洁
 */
export default {
  name: 'WarningCard',

  props: {
    /** 预警数据项 */
    item: {
      type: Object,
      required: true
    },
    /** 是否为资金模式 */
    isFundMode: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    /** 卡片标题 */
    title: function() {
      return this.isFundMode
        ? (this.item.shortName || '资金池')
        : (this.item.supProName || '未知产品')
    },

    /** 副标题 */
    subTitle: function() {
      return this.isFundMode
        ? '商户号: ' + this.item.supplierNo
        : '编码: ' + (this.item.supProCode || this.item.supplierNo)
    },

    /** 供应商名称 */
    supplierName: function() {
      return this.item.shortName || this.item.supplierNo || '供应商'
    },

    /** 格式化后的数值 */
    formattedValue: function() {
      var value = this.isFundMode
        ? this.item.remainingAmount
        : this.item.remainingStock
      var formatted = this.formatNumber(value || 0)
      return this.isFundMode ? '¥' + formatted : formatted + '张'
    },

    /** 格式化后的日消耗 */
    formattedDaily: function() {
      var formatted = this.formatNumber(this.item.dailyConsumption || 0)
      return this.isFundMode ? '¥' + formatted : formatted
    },

    /** 天数颜色样式类 */
    daysColorClass: function() {
      var days = this.parseDays(this.item.availableDays)
      if (days <= 7) return 'danger'
      if (days <= 30) return 'warning'
      return 'success'
    }
  },

  methods: {
    /** 数字格式化 */
    formatNumber: function(num) {
      return new Intl.NumberFormat('zh-CN').format(num)
    },

    /** 解析天数 */
    parseDays: function(dayStr) {
      if (!dayStr && dayStr !== 0 && dayStr !== '0') return 99999
      if (String(dayStr).indexOf('无限') !== -1) return 99999
      var num = parseFloat(dayStr)
      return isNaN(num) ? 99999 : num
    }
  }
}
</script>

<style scoped>
.warning-card {
  background: #fff;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #EBEEF5;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  display: flex;
  justify-content: space-between;
  align-items: stretch;
}

.card-left {
  flex: 1;
  padding-right: 12px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

/* 标签行 */
.tag-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.tag {
  font-size: 12px;
  padding: 2px 6px;
  border-radius: 4px;
}

.tag-province {
  background: #F0F2F5;
  color: #909399;
  font-weight: 500;
}

.tag-supplier {
  color: #909399;
  border: 1px solid #DCDFE6;
}

/* 标题区 */
.card-title {
  font-size: 16px;
  font-weight: bold;
  color: #303133;
  line-height: 1.4;
  margin: 0 0 4px 0;
}

.card-subtitle {
  font-size: 12px;
  color: #909399;
  font-family: monospace;
  margin: 0 0 12px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 数值区 */
.value-row {
  display: flex;
  align-items: flex-end;
  gap: 24px;
}

.value-item {
  display: flex;
  flex-direction: column;
}

.value-label {
  font-size: 10px;
  color: #909399;
  margin: 0 0 2px 0;
}

.value-num {
  font-size: 14px;
  font-weight: bold;
  color: #303133;
  font-family: monospace;
  margin: 0;
}

.value-num.secondary {
  font-weight: 500;
  color: #606266;
}

/* 右侧天数区 */
.card-right {
  min-width: 80px;
  padding: 8px 12px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.card-right.danger {
  background: #FEF0F0;
  border: 1px solid #FDE2E2;
}

.card-right.danger .days-num {
  color: #F56C6C;
}

.card-right.warning {
  background: #FDF6EC;
  border: 1px solid #FAECD8;
}

.card-right.warning .days-num {
  color: #E6A23C;
}

.card-right.success {
  background: #F0F9EB;
  border: 1px solid #E1F3D8;
}

.card-right.success .days-num {
  color: #67C23A;
}

.days-num {
  font-size: 20px;
  font-weight: bold;
  font-family: monospace;
}

.days-label {
  font-size: 10px;
  color: #909399;
  margin-top: 4px;
}
</style>
